import React, { useRef } from 'react';
import {
  Box,
  Typography,
  Divider,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton
} from '@mui/material';
import {
  PrinterIcon,
} from '@heroicons/react/24/outline';
import CloseButton from './CloseButton';
import { format } from 'date-fns';
import { CURRENCY } from '../config/currency';

interface MenuItem {
  id: number;
  name: string;
  description?: string;
  price: number;
}

interface ConsolidatedItem {
  menuItem: MenuItem;
  quantity: number;
  price: number;
  totalPrice: number;
}

interface Customer {
  username: string;
  email?: string;
  phone?: string;
}

interface Invoice {
  id: number;
  orderCustomOrderId: string;
}

interface ConsolidatedGroup {
  sessionCode: string;
  customer?: Customer;
  dateRange: {
    from: string;
    to: string;
  };
  invoiceCount: number;
  consolidatedItems?: ConsolidatedItem[];
  paidAmount?: number;
  invoices?: Invoice[];
  // Tax fields from backend
  subtotal?: number;
  cgstRate?: number;
  cgstAmount?: number;
  sgstRate?: number;
  sgstAmount?: number;
  igstRate?: number;
  igstAmount?: number;
  serviceChargeRate?: number;
  serviceChargeAmount?: number;
  calculatedTotal?: number;
  // Payment method
  paymentMethod?: string;
  paymentNotes?: string;
}

interface Theme {
  background: string;
  text: string;
  primary: string;
  gray?: {
    [key: string]: string;
  };
}

interface ConsolidatedInvoiceViewProps {
  group: ConsolidatedGroup;
  theme: Theme;
  onClose?: () => void;
}

const ConsolidatedInvoiceView: React.FC<ConsolidatedInvoiceViewProps> = ({ group, theme, onClose }) => {
  const invoiceRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    window.print();
  };

  // Use backend-provided subtotal or calculate from items
  const subtotal = group.subtotal ?? group.consolidatedItems?.reduce((sum: number, item: ConsolidatedItem) =>
    sum + (item.totalPrice || 0), 0) ?? 0;

  // Use actual tax values from backend
  const cgstAmount = group.cgstAmount ?? 0;
  const sgstAmount = group.sgstAmount ?? 0;
  const igstAmount = group.igstAmount ?? 0;
  const serviceChargeAmount = group.serviceChargeAmount ?? 0;

  // Use backend-calculated total or calculate with actual taxes
  const total = group.calculatedTotal ?? (subtotal + cgstAmount + sgstAmount + igstAmount + serviceChargeAmount);

  // Check if we have IGST (inter-state) or CGST+SGST (intra-state)
  const hasIgst = (group.igstRate ?? 0) > 0;
  const hasCgstSgst = (group.cgstRate ?? 0) > 0 || (group.sgstRate ?? 0) > 0;
  const hasServiceCharge = (group.serviceChargeRate ?? 0) > 0;

  return (
    <Box sx={{ 
      backgroundColor: theme.background, 
      minHeight: '100vh', 
      maxHeight: '100vh',
      overflow: 'auto',
      p: 2 
    }}>
      {/* Actions Bar */}
      <Box sx={{ 
        mb: 2, 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography 
          variant="h5" 
          sx={{ 
            color: theme.text,
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            textAlign: { xs: 'center', sm: 'left' }
          }}
        >
          Consolidated Invoice
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 1, sm: 1 },
          flexWrap: 'wrap',
          justifyContent: { xs: 'center', sm: 'flex-end' }
        }}>
          <Button
            variant="outlined"
            startIcon={<PrinterIcon style={{ width: 18, height: 18 }} />}
            onClick={handlePrint}
            sx={{
              backgroundColor: 'transparent !important',
              color: (theme.gray?.[700] || '#374151') + ' !important',
              border: `1px solid ${theme.gray?.[300] || '#d1d5db'} !important`,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              px: { xs: 1.5, sm: 2 },
              py: { xs: 0.5, sm: 0.75 },
              minHeight: { xs: '32px', sm: '36px' },
              boxShadow: 'none !important',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: (theme.gray?.[100] || '#f3f4f6') + ' !important',
                borderColor: (theme.gray?.[400] || '#9ca3af') + ' !important'
              }
            }}
          >
            Print
          </Button>
          {onClose && (
            <CloseButton onClick={onClose} size="md" />
          )}
        </Box>
      </Box>

      {/* Invoice Content */}
      <Paper
        ref={invoiceRef}
        id="consolidated-invoice-print"
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          backgroundColor: '#ffffff',
          color: '#000000',
          '@media print': {
            boxShadow: 'none',
            m: 0,
            '& *': {
              visibility: 'visible !important'
            },
            '& ~ *': {
              display: 'none'
            },
            'body > *:not(#consolidated-invoice-print)': {
              display: 'none'
            }
          }
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#000' }}>
            INVOICE
          </Typography>
          <Typography variant="subtitle1" sx={{ color: '#666' }}>
            Session: {group.sessionCode}
          </Typography>
        </Box>

        {/* Customer & Date Info */}
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            gap: { xs: 3, sm: 0 },
            mb: 4 
          }}
        >
          <Box>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontWeight: 'bold', 
                color: '#000',
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              Bill To:
            </Typography>
            <Typography 
              sx={{ 
                color: '#333',
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              {group.customer?.username || 'Guest Customer'}
            </Typography>
            {group.customer?.email && (
              <Typography 
                sx={{ 
                  color: '#666', 
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}
              >
                {group.customer.email}
              </Typography>
            )}
            {group.customer?.phone && (
              <Typography 
                sx={{ 
                  color: '#666', 
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}
              >
                {group.customer.phone}
              </Typography>
            )}
          </Box>
          
          <Box 
            sx={{ 
              textAlign: { xs: 'left', sm: 'right' }
            }}
          >
            <Typography 
              sx={{ 
                color: '#666', 
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }}
            >
              Date Range:
            </Typography>
            <Typography 
              sx={{ 
                color: '#333',
                fontSize: { xs: '0.75rem', sm: '1rem' }
              }}
            >
              {format(new Date(group.dateRange.from), 'MMM d, yyyy h:mm a')}
            </Typography>
            <Typography 
              sx={{ 
                color: '#333',
                fontSize: { xs: '0.75rem', sm: '1rem' }
              }}
            >
              to {format(new Date(group.dateRange.to), 'MMM d, yyyy h:mm a')}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Typography 
                sx={{ 
                  color: '#666', 
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}
              >
                Orders: {group.invoiceCount}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Items Table */}
        <TableContainer 
          sx={{ 
            mb: 3,
            overflowX: { xs: 'auto', sm: 'visible' },
            '&::-webkit-scrollbar': {
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#c1c1c1',
              borderRadius: '4px',
            },
          }}
        >
          <Table sx={{ minWidth: { xs: '500px', sm: 'auto' } }}>
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: '#000',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    padding: { xs: '8px 4px', sm: '16px' },
                    minWidth: { xs: '120px', sm: 'auto' }
                  }}
                >
                  Item
                </TableCell>
                <TableCell 
                  align="center" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: '#000',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    padding: { xs: '8px 4px', sm: '16px' },
                    minWidth: { xs: '50px', sm: 'auto' }
                  }}
                >
                  Qty
                </TableCell>
                <TableCell 
                  align="right" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: '#000',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    padding: { xs: '8px 4px', sm: '16px' },
                    minWidth: { xs: '70px', sm: 'auto' }
                  }}
                >
                  Rate
                </TableCell>
                <TableCell 
                  align="right" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: '#000',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    padding: { xs: '8px 4px', sm: '16px' },
                    minWidth: { xs: '80px', sm: 'auto' }
                  }}
                >
                  Amount
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {group.consolidatedItems?.map((item: ConsolidatedItem, index: number) => (
                <TableRow key={index}>
                  <TableCell 
                    sx={{ 
                      color: '#333',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      padding: { xs: '8px 4px', sm: '16px' },
                      minWidth: { xs: '120px', sm: 'auto' }
                    }}
                  >
                    {item.menuItem?.name}
                    {item.menuItem?.description && (
                      <Typography 
                        variant="caption" 
                        display="block" 
                        sx={{ 
                          color: '#666',
                          fontSize: { xs: '0.65rem', sm: '0.75rem' },
                          lineHeight: 1.2
                        }}
                      >
                        {item.menuItem.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell 
                    align="center" 
                    sx={{ 
                      color: '#333',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      padding: { xs: '8px 4px', sm: '16px' }
                    }}
                  >
                    {item.quantity}
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      color: '#333',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      padding: { xs: '8px 4px', sm: '16px' }
                    }}
                  >
                    {CURRENCY.symbol}{item.price.toFixed(2)}
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      color: '#333',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      padding: { xs: '8px 4px', sm: '16px' }
                    }}
                  >
                    {CURRENCY.symbol}{item.totalPrice.toFixed(2)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ mb: 2 }} />

        {/* Totals */}
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: { xs: 'center', sm: 'flex-end' }
          }}
        >
          <Box 
            sx={{ 
              width: { xs: '100%', sm: 250 },
              maxWidth: { xs: '300px', sm: '250px' }
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography 
                sx={{ 
                  color: '#666',
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
                Subtotal:
              </Typography>
              <Typography 
                sx={{ 
                  color: '#333',
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
                {CURRENCY.symbol}{subtotal.toFixed(2)}
              </Typography>
            </Box>
            {/* Show CGST + SGST for intra-state, or IGST for inter-state */}
            {hasCgstSgst && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography
                    sx={{
                      color: '#666',
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                  >
                    CGST ({group.cgstRate ?? 0}%):
                  </Typography>
                  <Typography
                    sx={{
                      color: '#333',
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                  >
                    {CURRENCY.symbol}{cgstAmount.toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography
                    sx={{
                      color: '#666',
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                  >
                    SGST ({group.sgstRate ?? 0}%):
                  </Typography>
                  <Typography
                    sx={{
                      color: '#333',
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                  >
                    {CURRENCY.symbol}{sgstAmount.toFixed(2)}
                  </Typography>
                </Box>
              </>
            )}
            {hasIgst && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography
                  sx={{
                    color: '#666',
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                >
                  IGST ({group.igstRate ?? 0}%):
                </Typography>
                <Typography
                  sx={{
                    color: '#333',
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                >
                  {CURRENCY.symbol}{igstAmount.toFixed(2)}
                </Typography>
              </Box>
            )}
            {hasServiceCharge && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography
                  sx={{
                    color: '#666',
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                >
                  Service Charge ({group.serviceChargeRate ?? 0}%):
                </Typography>
                <Typography
                  sx={{
                    color: '#333',
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                >
                  {CURRENCY.symbol}{serviceChargeAmount.toFixed(2)}
                </Typography>
              </Box>
            )}
            {/* Fallback if no tax config is available */}
            {!hasCgstSgst && !hasIgst && !hasServiceCharge && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography
                  sx={{
                    color: '#666',
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                >
                  Tax:
                </Typography>
                <Typography
                  sx={{
                    color: '#333',
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                >
                  {CURRENCY.symbol}0.00
                </Typography>
              </Box>
            )}
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 'bold', 
                  color: '#000',
                  fontSize: { xs: '1rem', sm: '1.25rem' }
                }}
              >
                Total:
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 'bold', 
                  color: '#000',
                  fontSize: { xs: '1rem', sm: '1.25rem' }
                }}
              >
                {CURRENCY.symbol}{total.toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Payment Status */}
        {group.paidAmount !== undefined && (
          <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography sx={{ color: '#666', fontSize: '0.875rem' }}>
              Payment Status:
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography sx={{ color: '#333' }}>
                Paid: {CURRENCY.symbol}{group.paidAmount.toFixed(2)}
              </Typography>
              <Typography sx={{ color: total - group.paidAmount > 0 ? '#d32f2f' : '#2e7d32' }}>
                {total - group.paidAmount > 0
                  ? `Due: ${CURRENCY.symbol}${(total - group.paidAmount).toFixed(2)}`
                  : 'Fully Paid'
                }
              </Typography>
            </Box>
            {/* Payment Method */}
            {group.paymentMethod && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
                <Typography sx={{ color: '#666', fontSize: '0.875rem' }}>
                  Payment Method:
                </Typography>
                <Typography sx={{ color: '#333', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span>
                    {group.paymentMethod === 'CASH' && '💵'}
                    {group.paymentMethod === 'CARD' && '💳'}
                    {group.paymentMethod === 'UPI' && '📱'}
                    {group.paymentMethod === 'WALLET' && '👛'}
                    {group.paymentMethod === 'MOBILE_PAY' && '📲'}
                    {group.paymentMethod === 'GIFT_CARD' && '🎁'}
                    {group.paymentMethod === 'OTHER' && '📝'}
                  </span>
                  {group.paymentMethod}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Individual Orders Reference */}
        <Box sx={{ mt: 4, p: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#000' }}>
            Order References:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {group.invoices?.map((invoice: Invoice) => (
              <Typography 
                key={invoice.id} 
                variant="caption" 
                sx={{ 
                  color: '#666',
                  backgroundColor: '#e0e0e0',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1
                }}
              >
                #{invoice.orderCustomOrderId}
              </Typography>
            ))}
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: '#999' }}>
            Thank you for your business!
          </Typography>
        </Box>
      </Paper>

      {/* Print styles are now handled via sx prop on Paper component */}
    </Box>
  );
};

export default ConsolidatedInvoiceView;